<template>
  <div class="project-note-card mb-4">
    <div class="d-flex flex-wrap justify-content-between align-items-start mb-3">
      <div class="d-flex align-items-start">
        <div class="project-note-avatar me-3">
          <i class="fa fa-user" />
        </div>
        <div>
          <div class="font-weight-bold">
            {{ authorName(note.user) }}
          </div>
          <div class="text-muted small">
            {{ noteTimestamp(note) }}
          </div>
        </div>
      </div>

      <div
        v-if="isOwnNote(note)"
        class="d-flex flex-wrap"
      >
        <b-button
          v-if="!isEditing"
          variant="outline-primary"
          size="sm"
          class="mr-2 mb-2"
          @click="$emit('start-edit', note)"
        >
          <i class="far fa-edit me-1" />
          Edit
        </b-button>
        <b-button
          variant="outline-danger"
          size="sm"
          class="mb-2"
          @click="$emit('delete-note', note.id)"
        >
          <i class="far fa-trash-alt me-1" />
          Delete
        </b-button>
      </div>
    </div>

    <div v-if="isEditing">
      <div class="form-group mb-3">
        <label :for="'project-note-edit-' + note.id">Edit note</label>
        <textarea
          :id="'project-note-edit-' + note.id"
          :value="value"
          class="form-control"
          rows="4"
          @input="$emit('input', $event.target.value.trim())"
        />
      </div>
      <div class="d-flex flex-wrap">
        <b-button
          variant="success"
          size="sm"
          class="mr-2 mb-2"
          :disabled="savingEdit || !value"
          @click="$emit('save-edit', note.id)"
        >
          <b-spinner
            v-if="savingEdit"
            small
            class="me-1"
          />
          Save
        </b-button>
        <b-button
          variant="secondary"
          size="sm"
          class="mb-2"
          :disabled="savingEdit"
          @click="$emit('cancel-edit', note.id)"
        >
          Cancel
        </b-button>
      </div>
    </div>

    <div v-else>
      <p class="mb-0 project-note-content">
        {{ note.content }}
      </p>
    </div>

    <div
      v-if="note.files && note.files.length"
      class="mt-4"
    >
      <div class="small text-uppercase text-muted font-weight-bold mb-2">
        Attachments
      </div>
      <div
        v-for="file in note.files"
        :key="file.id"
        class="project-notes-file-row d-flex flex-wrap align-items-center justify-content-between"
      >
        <div class="d-flex align-items-center text-break me-3">
          <i :class="['fa', fileIcon(file.mime_type), 'text-muted', 'me-2']" />
          <span class="me-2">{{ file.original_name }}</span>
          <small class="text-muted">{{ formatFileSize(file.file_size) }}</small>
        </div>
        <div class="d-flex flex-wrap">
          <b-button
            variant="outline-info"
            size="sm"
            class="mr-2 mb-2"
            @click="$emit('download-file', file.id)"
          >
            <i class="fa fa-download me-1" />
            Download
          </b-button>
          <b-button
            v-if="isSuperAdmin"
            variant="outline-danger"
            size="sm"
            class="mb-2"
            @click="$emit('delete-file', file.id)"
          >
            <i class="fa fa-times me-1" />
            Remove File
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
    name: "ProjectNoteItem",
    props: {
        note: {
            type: Object,
            required: true,
        },
        authUserId: {
            type: Number,
            required: true,
        },
        isSuperAdmin: {
            type: Boolean,
            default: false,
        },
        isEditing: {
            type: Boolean,
            default: false,
        },
        savingEdit: {
            type: Boolean,
            default: false,
        },
        value: {
            type: String,
            default: "",
        },
    },
    methods: {
        authorName(user) {
            return `${user.first_name} ${user.last_name}`;
        },
        noteTimestamp(note) {
            const updatedAt = note.updated_at && note.updated_at !== note.created_at;

            if (updatedAt) {
                return `${this.$moment(note.updated_at).fromNow()} (edited)`;
            }

            return this.$moment(note.created_at).fromNow();
        },
        isOwnNote(note) {
            return Number(note.user_id) === Number(this.authUserId);
        },
        formatFileSize(bytes) {
            if (!bytes) {
                return "0 B";
            }

            const units = ["B", "KB", "MB", "GB"];
            const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
            const size = bytes / Math.pow(1024, exponent);

            return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
        },
        fileIcon(mimeType = "") {
            if (mimeType.includes("pdf")) {
                return "fa-file-pdf";
            }

            if (mimeType.includes("word") || mimeType.includes("document")) {
                return "fa-file-word";
            }

            if (mimeType.includes("text")) {
                return "fa-file-alt";
            }

            return "fa-file";
        },
    },
};
</script>

<style scoped>
.project-note-card {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 1.25rem;
    background: #fff;
}

.project-note-avatar {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f6f9;
    color: #5c80d1;
}

.project-note-content {
    white-space: pre-line;
    color: #2f3542;
    line-height: 1.6;
}

.project-notes-file-row {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    background: #f8f9fa;
}

.project-notes-file-row + .project-notes-file-row {
    margin-top: 0.75rem;
}

@media (prefers-reduced-motion: reduce) {
    .project-note-card,
    .project-notes-file-row {
        transition: none;
    }
}
</style>
